import { useRef, useState } from 'react'
import { HTMLMessageDescriptor } from '@web/walter-lib/react-intl'
import { useCallbackRef, useNotification } from '@web/walter-ui-kit'
import { getErrorNotification } from '~hooks/usePromise/utils'
import useToast from '~hooks/useToast'

interface Params<R> {
    onSuccess?: (result?: R) => void
    toastCaption?: HTMLMessageDescriptor
    showErrorNotification?: boolean
    onError?: (error?: unknown) => void
    initialLoading?: boolean
}

type PromiseResult<T extends Promise<any>> = T extends Promise<infer R> ? R : never

export const usePromise = <T extends (...args: any[]) => Promise<any>, R = PromiseResult<ReturnType<T>>>(
    cb: T,
    params?: Params<R>
) => {
    const { onSuccess, toastCaption, showErrorNotification = true, onError, initialLoading = false } = params || {}
    const [isLoading, setLoading] = useState(initialLoading)
    const [data, setData] = useState<R | undefined>()
    const [error, setError] = useState<unknown>()

    const { push: pushError } = useNotification()
    const pushToast = useToast({ caption: toastCaption, dataPointer: 'toast_caption' })

    const callIdRef = useRef(0)

    const send = useCallbackRef(async (...args: Parameters<T>) => {
        const currentId = ++callIdRef.current

        setLoading(true)

        try {
            const result = await cb(...args)
            if (currentId !== callIdRef.current) {
                return
            }
            toastCaption && pushToast()
            onSuccess?.(result)
            setData(result)
            setError(undefined)
        } catch (err) {
            //eslint-disable-next-line
            console.log(err)
            if (currentId !== callIdRef.current) {
                return
            }
            showErrorNotification && pushError(getErrorNotification(err))
            onError?.(err)
            setData(undefined)
            setError(err)
        }

        setLoading(false)
    })

    const reset = useCallbackRef(() => {
        ++callIdRef.current

        setLoading(false)
        setData(undefined)
        setError(undefined)
    })

    return { isLoading, data, error, send, reset }
}
